/**
 * Restricciones de carpeta (spec 2026-10-07 §3). Módulo puro.
 *
 * Una restricción es { path, uids }: `path` es la ruta de la COLECCIÓN de la
 * carpeta (folders/{cid}/{id1}/{doc}/{id2}…). U no ve la carpeta C si alguna
 * restricción con U en `uids` tiene `path` igual a la ruta de C o prefijo de
 * ella por SEGMENTOS completos (nunca coincidencias parciales de id). Las
 * restricciones nunca se levantan hacia abajo. A root nunca se le aplica: lo
 * decide quien llama (lib/company_access).
 *
 * El doc vive en folders/{cid}/restricciones/{docId}; docId = path con '/'
 * reemplazado por '~' (determinista: un doc por carpeta). Los ids de carpeta
 * salen de btoa de dígitos o de ids automáticos: nunca traen '~'.
 *
 * COPIA CJS en daysi-cloud-functions/scripts/folder_restrictions_lib.js;
 * folder_restrictions.test.js exige que den lo mismo. Si cambias uno, cambia
 * el otro.
 */
import { collectionPathOfPointer } from './folder_chain';

export const RESTRICCIONES = 'restricciones';

const RESERVED_ID = /^__.*__$/;
const segs = (p) => (typeof p === 'string' ? p.split('/').filter(Boolean) : []);

/** folders/{cid}/{id}(/{doc}/{id})*, sin barras sobrantes. */
export const isFolderCollectionPath = (p) => {
  const s = segs(p);
  return s.length >= 3 && s.length % 2 === 1 && s[0] === 'folders' && s.join('/') === p;
};

export const docIdForPath = (collectionPath) => {
  if (!isFolderCollectionPath(collectionPath) || collectionPath.includes('~')) return null;
  const id = collectionPath.split('/').join('~');
  return RESERVED_ID.test(id) ? null : id;
};

export const normalizeRestriction = (data) => {
  if (!data || typeof data !== 'object' || !isFolderCollectionPath(data.path) || !Array.isArray(data.uids)) return null;
  return { path: data.path, uids: [...new Set(data.uids.filter((u) => typeof u === 'string' && u))] };
};

export const normalizeRestrictions = (list) => (Array.isArray(list) ? list : [])
  .map(normalizeRestriction)
  .filter(Boolean);

const covers = (restrictionPath, collectionPath) => collectionPath === restrictionPath
  || collectionPath.startsWith(`${restrictionPath}/`);

export const restrictionsFor = (collectionPath, uid, restrictions) => {
  if (!uid || typeof collectionPath !== 'string') return [];
  return normalizeRestrictions(restrictions).filter((r) => r.uids.includes(uid) && covers(r.path, collectionPath));
};

export const isRestricted = (collectionPath, uid, restrictions) => restrictionsFor(collectionPath, uid, restrictions).length > 0;

/** Para «Restringido desde ‹nombre›»: el ancestro ESTRICTO más alto que restringe a uid. */
export const restrictedSourceFor = (collectionPath, uid, restrictions) => {
  const list = restrictionsFor(collectionPath, uid, restrictions).filter((r) => r.path !== collectionPath);
  if (!list.length) return null;
  return list.sort((a, b) => segs(a.path).length - segs(b.path).length)[0].path;
};

export const ownRestriction = (collectionPath, restrictions) => normalizeRestrictions(restrictions)
  .find((r) => r.path === collectionPath) || null;

/** null si el puntero no es el de un archivo de carpeta (p. ej. files/{id} de un adjunto). */
export const fileIsRestricted = (pointerDocPath, uid, restrictions) => {
  const cp = collectionPathOfPointer(pointerDocPath);
  return cp ? isRestricted(cp, uid, restrictions) : null;
};

export default isRestricted;
